// No sort control, deliberately: rows keep one fixed order while prices tick, so
// a row the eye is on never moves.
import { isClosed, RECOMMENDATION_FILTERS } from '@alphadesk/ontology/objects/recommendation.ts';
import type {
  Viewer,
  WireAnalystRef,
  WireQuote,
  WireRecommendation,
  WireRoiRecommendation,
} from '@alphadesk/web/contract.ts';
import { useEffect, useRef, useState } from 'react';
import { MARKET_LENSES, type MarketLens } from './market.ts';
import { useQueryState } from './router.ts';
import type { Scope } from './scope.ts';

type RecommendationStatusFilter = (typeof RECOMMENDATION_FILTERS)[number];

export const AGGREGATE_THROTTLE_MS = 3000;

export interface ViewState {
  recommendationStatus: RecommendationStatusFilter;
  market: MarketLens;
}

export const DEFAULT_VIEW: ViewState = {
  recommendationStatus: 'open',
  market: 'all',
};

/** `raw`, if it is one of `values`: for what a URL or storage hands back. */
export function oneOf<T extends string>(values: readonly T[], raw: unknown): T | null {
  return typeof raw === 'string' && (values as readonly string[]).includes(raw) ? (raw as T) : null;
}

const QUERY_KEYS = ['recommendations', 'market'] as const;

function parseView(search: string): ViewState {
  const q = new URLSearchParams(search);
  return {
    recommendationStatus:
      oneOf(RECOMMENDATION_FILTERS, q.get('recommendations')) ?? DEFAULT_VIEW.recommendationStatus,
    market: oneOf(MARKET_LENSES, q.get('market')) ?? DEFAULT_VIEW.market,
  };
}

// Touches no query key it does not own.
function serialiseView(view: ViewState, search: string): string {
  const q = new URLSearchParams(search);
  for (const key of QUERY_KEYS) q.delete(key);
  if (view.recommendationStatus !== DEFAULT_VIEW.recommendationStatus)
    q.set('recommendations', view.recommendationStatus);
  if (view.market !== DEFAULT_VIEW.market) q.set('market', view.market);
  return q.toString();
}

export const useViewState = () => useQueryState(parseView, serialiseView);

export function useThrottled(value: number, ms: number): number {
  const [held, setHeld] = useState(value);
  const settledAt = useRef(0);

  useEffect(() => {
    const wait = settledAt.current + ms - Date.now();
    if (wait <= 0) {
      settledAt.current = Date.now();
      setHeld(value);
      return;
    }
    const timer = setTimeout(() => {
      settledAt.current = Date.now();
      setHeld(value);
    }, wait);
    return () => clearTimeout(timer);
  }, [value, ms]);

  return held;
}

export function matchesRecommendationStatus(
  recommendation: WireRecommendation,
  filter: RecommendationStatusFilter,
): boolean {
  if (filter === 'all') return true;
  return filter === 'open' ? !isClosed(recommendation.status) : isClosed(recommendation.status);
}

// Must match the order the server lists recommendations in.
export function orderRecommendations(rows: readonly WireRecommendation[]): WireRecommendation[] {
  return [...rows].sort((a, b) => b.recommendedOn.localeCompare(a.recommendedOn) || b.id - a.id);
}

export interface TradesView {
  analysts: WireAnalystRef[];
  /** Uncapped; RoI is computed over this, never the list. */
  record: WireRoiRecommendation[];
  quotes: ReadonlyMap<number, WireQuote>;
  viewer: Viewer;
  view: ViewState;
  patch: (change: Partial<ViewState>) => void;
  scope: Scope;
  onScope: (scope: Scope) => void;
  revision: number;
}
